import { validateCensusRow } from './validateCensusRow';

const normalizeRelationship = (relationship) => {
  const value = (relationship ?? '').toString().trim().toLowerCase();

  if (value === 'employee' || value === 'ee' || value === 'self') {
    return 'employee';
  }

  if (value === 'spouse' || value === 'sp') {
    return 'spouse';
  }

  if (value === 'child' || value === 'ch') {
    return 'child';
  }

  return value;
};

export const validateCensus = (json, localCensus, setIsLoadingFile) => {
  let rows = {};

  for (const [i, value] of json.entries()) {
    const life = value;

    let errors = [];

    if (life?.current_medical_plan_name?.toString().toLowerCase()?.includes('waive')) {
      life.current_medical_plan_name = '';
    }
    if (life?.dental_plan_name?.toString().toLowerCase()?.includes('waive')) {
      life.dental_plan_name = '';
    }
    if (life?.vision_plan_name?.toString().toLowerCase()?.includes('waive')) {
      life.vision_plan_name = '';
    }

    if (!life?.first_name) {
      errors?.push(`Error: Row ${i + 1}: Missed 'first_name' value`);
    }
    if (!life?.last_name) {
      errors?.push(`Error: Row ${i + 1}: Missed 'last_name' value`);
    }

    if (!life?.gender) {
      errors?.push(`Error: Row ${i + 1}: Missed 'gender' value`);
    }

    if (life?.gender?.toString().toLowerCase() === 'male') {
      life.gender = 'M';
    }
    if (life?.gender?.toString().toLowerCase() === 'female') {
      life.gender = 'F';
    }

    if (!life.relationship) {
      errors.push(`Error: Row ${i + 1}: Missed 'relationship' value`);
    } else {
      life.relationship = normalizeRelationship(life.relationship);
    }

    if (
      life.relationship?.toString().toLowerCase() === 'domestic_partner' ||
      life.relationship?.toString().toLowerCase() === 'domestic partner'
    ) {
      life.relationship = 'life_partner';
    }

    if (life?.salary_amount) {
      life.salary_amount = !isNaN(parseInt(life?.salary_amount, 10))
        ? parseInt(life?.salary_amount, 10)
        : life?.salary_amount;
    }

    if (life?.life_benefit_amount) {
      life.life_benefit_amount = !isNaN(parseInt(life?.life_benefit_amount, 10))
        ? parseInt(life?.life_benefit_amount, 10)
        : life?.life_benefit_amount;
    }

    try {
      validateCensusRow(life, errors);
    } catch (err) {
      setIsLoadingFile(false);
      errors?.push(`Error: Row ${i + 1}: ${err?.toString()}`);
    }

    if (!life.ee_id) {
      errors?.push(`Error: Row ${i + 1}: Missed 'ee_id' value`);
    }

    if (life?.ee_id?.toString()?.match(/[!@#\\$%\\^\\&*\\)\\[\]{}\\/\\|(+=.,:;?_'"`<>~-]/g)) {
      errors?.push(
        `Error: Row ${
          i + 1
        }: Enter valid 'ee_id' value.characters that should not exist: !?@#$%^&*()_+-{}[]\\|/:;"'<>,.`,
      );
    }

    if (!life.ee_id) {
      errors?.push(`Error: Row ${i + 1}: Missed 'ee_id' value`);
    }

    life.ee_id = life.ee_id?.toString();

    const zipcodeLengthRegex = /^([0-9]){5,5}$/;
    const vaildZipcodeRegex = /^(\d{5,5})+$/;

    if (!life?.zip_code?.toString()?.match(vaildZipcodeRegex)) {
      errors?.push(`Error: Row ${i + 1}: 'zip_code' value should be a number`);
    }

    if (!life?.zip_code?.toString()?.match(zipcodeLengthRegex)) {
      errors?.push(`Error: Row ${i + 1}: 'zip_code' value must be a 5 digits`);
    }

    if (!life.zip_code) {
      errors?.push(`Error: Row ${i + 1}: Missed 'zip_code' value`);
    }

    life.zip_code = life.zip_code?.toString();

    life.job_title = life.job_title ?? '';

    rows = { ...rows, [i + 1]: [...new Set(errors)] };

    localCensus.items.push(life);
  }

  for (const row in rows) {
    if (!rows[row]?.length) {
      delete rows[row];
    }
  }

  return rows || {};
};

export const validateCensusHousehold = (members = []) => {
  if (!members.length) return {};

  const householdFamilies = members.reduce((prev, item) => {
    const id = item.ee_id;

    if (typeof prev[id] === 'undefined') {
      prev[id] = [];
    }

    prev[id].push(item);

    return prev;
  }, {});

  let payload = {};

  for (const ee_id in householdFamilies) {
    const errors = [];
    const household = householdFamilies[ee_id];

    const employeeList = household.filter((item) => item.relationship.toLowerCase() === 'employee');

    const isEmployee = employeeList.length > 0;

    if (!isEmployee) {
      errors.push(`There is no employee in the household: ee_id ${ee_id}`);
    }

    if (employeeList.length > 1) {
      errors.push(`There cannot be two employees in the same household: ee_id ${ee_id}`);
    }

    const firstDefinedZip = household.find((item) => item.zip_code)?.zip_code;

    if (firstDefinedZip) {
      const zipMismatch = household.find((item) => item.zip_code !== firstDefinedZip);

      if (zipMismatch) {
        errors.push(`All members in ee_id ${ee_id} must have the same zip_code`);
      }

      const missingZipWhileOthersHave = household.find((item) => item.zip_code == null);

      if (missingZipWhileOthersHave) {
        errors.push(`All members in ee_id ${ee_id} must have a zip_code when any member has one`);
      }
    }

    const validatePlan = (data, key) => {
      if (!isEmployee) return;

      const employee = employeeList[0];

      const employeePlan = (employee?.[key] ?? '').toString().trim();
      const hasEmployeePlan = !!employeePlan;

      const dependents = data.filter(
        (item) => item.relationship && item.relationship.toLowerCase() !== 'employee',
      );

      if (!hasEmployeePlan) {
        const enrolledDependent = dependents.find((item) => {
          const plan = (item[key] ?? '').toString().trim();

          return !!plan;
        });

        if (enrolledDependent) {
          errors.push(
            `Dependents in ee_id ${ee_id} cannot be enrolled in ${key} when the employee is waived`,
          );
        }

        return;
      }

      const invalidMember = data.find((item) => {
        const plan = (item[key] ?? '').toString().trim();

        return plan && plan !== employeePlan;
      });

      if (invalidMember) {
        errors.push(
          `All members in ee_id ${ee_id} must either be enrolled in the employee's ${key} or waived`,
        );
      }
    };

    validatePlan(household, 'current_medical_plan_name');
    validatePlan(household, 'dental_plan_name');
    validatePlan(household, 'vision_plan_name');

    payload[ee_id] = errors;
  }

  for (const ee_id in payload) {
    if (!payload[ee_id]?.length) {
      delete payload[ee_id];
    }
  }

  return payload || {};
};
